/**
 * WorkingNotesSection — canonical "Working Notes" outline section.
 *
 * A stack of free-form, dated notes the team uses for extra context — NOT tasks, NOT the
 * Activity Log (a dated log of discrete lifecycle events), NOT a Thread's Description (its
 * original opening note). Mounted under both Threads (ThreadNode.jsx) and Milestones
 * (MilestoneSections.jsx) — same component, per CLAUDE.md's one-component-per-node-type rule.
 *
 * Deliberately NOT built on SectionNode/the outline engine's native zoom — mirrors
 * ThreadActivitySection/ThreadSubtasksSection/ThreadAttachmentsSection, the existing precedent
 * for a simple, non-zoomable, React-only section nested under a Thread. Consistent behavior
 * between the Thread and Milestone mounts matters more here than each entity's usual chrome.
 *
 * Add / edit / delete all happen from an ⋯ menu: "Add working note" on the section header,
 * "Edit note" / "Delete" on each row. Notes stack newest-first by entered date; editing changes
 * only the text — the original "entered by / on" byline never changes.
 */

import { useEffect, useRef } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { getMemberName } from '../outline/utils.js'
import { getWorkingNotes, createWorkingNote, updateWorkingNote, deleteWorkingNote } from '../services/workingNotes.js'
import { confirmDialog, alertDialog, promptDialog } from './Dialog.jsx'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function fmtDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return MONTHS[d.getMonth()] + ' ' + d.getDate()
}

/* createdByMemberId is a MEMBERS-table id (wf.currentMemberId) — not a Contacts id, so it must
   resolve via ctx.membersCache (getMemberName), the same fix ThreadNode.jsx's Activity Log
   needed. getMemberName falls back to the raw record id if unresolved; guard that away here. */
function resolveMemberName(id) {
  if (!id) return ''
  const name = getMemberName(id)
  return (name && name !== id) ? name : ''
}

function LoadingRow() {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: '#7a9aaa' }}>Loading…</span>
        </div>
      </div>
    </div>
  )
}

function EmptyRow() {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: '#7a9aaa', fontStyle: 'italic' }}>No working notes yet.</span>
        </div>
      </div>
    </div>
  )
}

function WorkingNoteRow({ note, cacheKeyStr }) {
  const menu = useActionMenu()
  const authorName = resolveMemberName(note.createdByMemberId)

  async function editNote() {
    const text = await promptDialog({ title: 'Edit working note', defaultValue: note.note, multiline: true, confirmLabel: 'Save' })
    if (text === null) return
    const trimmed = text.trim()
    if (!trimmed || trimmed === note.note) return
    try {
      const updated = await updateWorkingNote(note.id, trimmed)
      note.note = updated.note
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Save failed', message: e.message || String(e) }) }
  }

  async function deleteNote() {
    if (!(await confirmDialog({ title: 'Delete working note?', message: 'Delete this note? This cannot be undone.', confirmLabel: 'Delete', danger: true }))) return
    try {
      await deleteWorkingNote(note.id)
      const arr = ctx.workingNotesCache?.[cacheKeyStr]
      if (Array.isArray(arr)) { const idx = arr.findIndex(n => n.id === note.id); if (idx !== -1) arr.splice(idx, 1) }
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Delete failed', message: e.message || String(e) }) }
  }

  return (
    <div className="wf-node wf-node--item" data-key={'working-note:' + note.id}>
      <div className="wf-row" style={{ alignItems: 'flex-start' }}>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ marginTop: 6 }}></span>
        <div className="wf-text-wrap" style={{ minWidth: 0, flexDirection: 'column', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', width: '100%', justifyContent: 'flex-end', gap: 8 }}>
            {authorName && <span style={{ opacity: 0.55, whiteSpace: 'nowrap' }}>{authorName}</span>}
            <span className="wf-date-btn" style={{ pointerEvents: 'none', flexShrink: 0 }}>{fmtDate(note.createdAt)}</span>
          </div>
          <div className="wf-node-text" style={{ color: 'rgba(7,39,45,0.7)', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word', marginTop: 2, lineHeight: 1.45 }}>
            {note.note}
          </div>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={editNote}>Edit note</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={deleteNote} danger>Delete</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/**
 * @param {'thread'|'milestone'} entityType
 * @param {string} entityId — the Thread or Milestone's Airtable record id
 */
export default function WorkingNotesSection({ entityType, entityId }) {
  const cacheKeyStr = entityType + ':' + entityId
  const key = 'worknotes:' + cacheKeyStr
  const isCollapsed = wf.collapsed[key] !== false
  const notes = ctx.workingNotesCache?.[cacheKeyStr]  // undefined=not started, null=in-flight, []=loaded
  const menu = useActionMenu()
  const loadedRef = useRef(false)

  function load() {
    if (!ctx.workingNotesCache) ctx.workingNotesCache = {}
    ctx.workingNotesCache[cacheKeyStr] = null
    getWorkingNotes(entityType, entityId).then(list => {
      if (!ctx.workingNotesCache) ctx.workingNotesCache = {}
      ctx.workingNotesCache[cacheKeyStr] = list
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      if (!ctx.workingNotesCache) ctx.workingNotesCache = {}
      ctx.workingNotesCache[cacheKeyStr] = []
      window.dispatchEvent(new Event('outline-render'))
    })
  }

  // Load notes when expanded. "Entered by" resolves against ctx.membersCache (Members table,
  // loaded once at app startup) — guard here too in case that hasn't happened yet.
  useEffect(() => {
    if (isCollapsed) return
    if (!ctx.membersCache && ctx.loadMembers) {
      ctx.loadMembers(() => window.dispatchEvent(new Event('outline-render')))
    }
    if (loadedRef.current) return
    loadedRef.current = true
    load()
  }, [isCollapsed]) // eslint-disable-line react-hooks/exhaustive-deps

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  async function handleAdd() {
    const text = await promptDialog({ title: 'Add working note', placeholder: 'Context for the team…', multiline: true, confirmLabel: 'Add' })
    if (text === null) return
    const trimmed = text.trim()
    if (!trimmed) return
    try {
      const row = await createWorkingNote(entityType, entityId, trimmed, wf.currentMemberId)
      if (!ctx.workingNotesCache) ctx.workingNotesCache = {}
      const arr = ctx.workingNotesCache[cacheKeyStr]
      ctx.workingNotesCache[cacheKeyStr] = Array.isArray(arr) ? [row, ...arr] : [row]
      loadedRef.current = true
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) { alertDialog({ title: 'Save failed', message: e.message || String(e) }) }
  }

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" title="Working Notes"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Working Notes</span>
          {Array.isArray(notes) && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{notes.length}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={handleAdd}>Add working note</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          {(notes === null || notes === undefined) && <LoadingRow />}
          {Array.isArray(notes) && notes.length === 0 && <EmptyRow />}
          {Array.isArray(notes) && notes.map(n => (
            <WorkingNoteRow key={n.id} note={n} cacheKeyStr={cacheKeyStr} />
          ))}
        </div>
      )}
    </div>
  )
}
